import { useMemo, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button, Alert } from "react-bootstrap";
import { useDrag, useDrop } from 'react-dnd'

import { useGetUser, useHasPermission } from "../../../../common/Hooks.jsx";
import GenericDropdown from "../../../controls/GenericDropdown.jsx";
import Icon from "../../../controls/Icon.jsx";
import Forms from "../../../../services/forms.js";
import PubSub from "../../../../services/PubSub.js";
import Deployment from "../../../../services/deployment.js";
import EditFormTemplate from "./modals/EditFormTemplate.jsx";
import Utils from "../../../../utils/utils.js";
import EditSection from "./modals/EditSection.jsx";
import ConfirmationPrompt from "../../../controls/ConfirmationPrompt.jsx";
import { useFormsConfig } from "../Context.js";
import PublishTemplate from "./modals/PublishTemplate.jsx";
import TemplateVersionHistory from "./modals/TemplateVersionHistory.jsx";
import DisplayItemUsageInConditions from "./modals/DisplayItemUsageInConditions.jsx";
import mockPhoto from './mockPhoto.png';
import FORMS_DND_ITEMS from "../../../../config/forms-dnd.js";
import { copySection } from "../utils.js";
import SectionItems from "./SectionItems.jsx";

const FormTemplateEditor = () => {
    const { id: deploymentId, objectSubTypeId: formTemplateName, objectSubTypeVersion: formTemplateVersion } = useParams();
    const { formTemplate, setFormTemplate, guestMode, logAmplitudeEvent, isSectionUsedInFormConditions } = useFormsConfig();
    const [user] = useGetUser();

    const [translate] = useTranslation();
    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        deployment: deploymentId,
        type: "forms"
    });

    const [isLoading, setIsLoading] = useState(false);

    const updatePermissions = useMemo(() => !isLoading && formTemplate && formTemplate._id && permissions.update && !formTemplate.isPublished, [formTemplate, permissions.update, isLoading]);
    const deletePermissions = useMemo(() => !isLoading && formTemplate && formTemplate._id && permissions.delete && !formTemplate.isPublished, [formTemplate, permissions.delete, isLoading]);

    const [showAddSection, setShowAddSection] = useState(false);
    const [showEditSection, setShowEditSection] = useState(false);
    const [showDeleteSection, setShowDeleteSection] = useState(false);
    const [copiedSection, setCopiedSection] = useState({});
    const [showEditFormTemplate, setShowEditFormTemplate] = useState(false);
    const [showPublishTemplate, setShowPublishTemplate] = useState(false);
    const [showTemplateVersionHistory, setShowTemplateVersionHistory] = useState(false);
    const [allowPublish, setAllowPublish] = useState({ allow: false, message: "" });

    const [lastLivePreviewUpdate, setLastLivePreviewUpdate] = useState({});
    const [deploymentBaseUrl, setDeploymentBaseUrl] = useState("");
    const [livePreviewUrl, setLivePreviewUrl] = useState("");
    const [livePreviewWindow, setLivePreviewWindow] = useState(null);

    const [selectedSection, setSelectedSection] = useState();
    const [selectedSectionId, setSelectedSectionId] = useState();

    const getTemplate = useCallback(async ({ formTemplateName, formTemplateVersion }) => {
        const response = await Forms.getTemplateByNameVersion({ deploymentId, name: formTemplateName, version: formTemplateVersion });
        if (!response.error && response.data) {
            setFormTemplate(response.data);
            setSelectedSection(prev => {
                if (!prev) {
                    return response.data.sections[0]?.name || null;
                }

                if (!response.data.sections.map(section => section.name).includes(prev)) {
                    return response.data.sections[0]?.name || null;
                }

                return prev;
            });

            setAllowPublish(() => {
                if (response.data.sections.length === 0) {
                    return { allow: false, message: translate("OTM_CANNOT_PUBLISH_NO_SECTIONS") };
                }
                else {
                    const empty = response.data.sections.filter(section => (section?.items || []).length === 0);
                    if (empty.length === 0) {
                        return { allow: true, message: "" };
                    }
                    else {
                        return { allow: false, message: `${translate("OTM_CANNOT_PUBLISH_EMPTY_SECTIONS")} ${empty.map(section => section.name).join(", ")}` };
                    }
                }
            });
        }
    }, [deploymentId, setFormTemplate, setAllowPublish, translate]);

    const getDeployment = useCallback(async (id) => {
        if (user.isGuest) {
            const response = await Forms.getLivePreviewUrl({ deploymentId: id });
            if (!response.error) {
                if (response.data.livePreviewUrl) {
                    setDeploymentBaseUrl(`https://${response.data.livePreviewUrl}`);
                }
            }
        }
        else {
            const response = await Deployment.getDeployment(id);
            if (!response.error) {
                const url = response.data.cname ? response.data.cname : response.data.otmApp.cloudfrontUrl;
                if (url) {
                    setDeploymentBaseUrl(`https://${url}${response.data.homePage}`);
                }
            }
        }
    }, [user]);

    useEffect(() => {
        if (formTemplate && formTemplate?._id && deploymentBaseUrl) {
            setLivePreviewUrl(`${deploymentBaseUrl}/form?livePreview=true#RecordId=${formTemplate._id}`);
        }
    }, [formTemplate, deploymentBaseUrl]);

    const convertToDays = (number) => 1000 * 60 * 60 * 24 * number;

    const getBase64 = async (file) => {
        const response = await fetch(file);
        const blob = await response.blob();

        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;

            reader.readAsDataURL(blob);
        });
    }

    const sendLivePreviewUpdate = useCallback(async (openedWindow) => {
        if (lastLivePreviewUpdate.version !== formTemplate.__v) {
            setIsLoading(true);
            var formConfig = {};

            var response = await Forms.buildPreview({ deploymentId, formTemplateId: formTemplate._id });
            if (!response.error) {
                formConfig = response.data;
            }

            const sendData = {
                source: "livePreview",
                formConfig,
                formInstance: {
                    _id: formTemplate._id,
                    formName: formTemplate.name,
                    formDescription: formTemplate.description,
                    version: formTemplate.version,
                    name: formTemplate.name,
                    status: 'IN_PROGRESS',
                    dueDate: new Date(new Date().getTime() + convertToDays(1)),
                    expiryDate: new Date(new Date().getTime() + convertToDays(7)),
                    sections: formTemplate.sections.map((section) => ({
                        name: section.name,
                        items: section.items ? section.items.map((item) => ({
                            name: item.name,
                            dataType: item.dataType,
                            isRequired: item.isRequired
                        })) : [],
                        order: section.order
                    })),
                    data: guestMode ? {
                        'Security_and_Sign_Off': {
                            'Employee Photo': {
                                "Id": "9e3d395dbbc03e2",
                                "name": "employeePhoto.png",
                                "type": "image/png",
                                "contents": await getBase64(mockPhoto)
                            }
                        }
                    } : undefined
                }
            };
            openedWindow.postMessage(sendData, "*");
            setLastLivePreviewUpdate({ version: formTemplate.__v, sendData });
            setIsLoading(false);
        }
        else {
            openedWindow.postMessage(lastLivePreviewUpdate.sendData, "*");
        }
    }, [deploymentId, formTemplate, lastLivePreviewUpdate, guestMode]);

    useEffect(() => {
        if (deploymentId) {
            getDeployment(deploymentId);
        }
    }, [deploymentId, getDeployment]);

    useEffect(() => {
        if (formTemplateName && formTemplateVersion) {
            getTemplate({ formTemplateName, formTemplateVersion });
        }
    }, [formTemplateName, formTemplateVersion, getTemplate]);

    useEffect(() => {
        const section = formTemplate?.sections.find((section) => section.name === selectedSection);

        if (section) {
            setSelectedSectionId(section._id);
        }
    }, [selectedSection, formTemplate]);

    useEffect(() => {
        if (formTemplate && lastLivePreviewUpdate.version !== formTemplate.__v && livePreviewWindow && !livePreviewWindow.closed) {
            sendLivePreviewUpdate(livePreviewWindow);
        }
    }, [formTemplate, livePreviewWindow, sendLivePreviewUpdate, lastLivePreviewUpdate.version]);

    useEffect(() => {
        const subscriptionId = PubSub.subscribe("refresh-form-template", () => {
            getTemplate({ formTemplateName, formTemplateVersion });
        });

        return () => PubSub.unsubscribe("refresh-form-template", subscriptionId);
    }, [formTemplateName, formTemplateVersion, getTemplate]);

    const handlePublishTemplate = useCallback(async (values) => {
        setShowPublishTemplate(false);
        setIsLoading(true);
        const response = await Forms.publishTemplate({
            deploymentId,
            formTemplateId: formTemplate._id,
            body: values
        });

        if (!response.error) {
            PubSub.publish("show_info", translate("OTM_SUCCESS_PUBLISH_FORM_TEMPLATE"), translate("OTM_INFO"));
        }
        setIsLoading(false);
        PubSub.publish('refresh-form-template');
    }, [deploymentId, formTemplate, translate]);

    const handleDeleteSection = useCallback(async () => {
        setIsLoading(true);
        const section = formTemplate?.sections.find((section) => section.name === selectedSection);
        const result = isSectionUsedInFormConditions({ sectionId: section._id });

        if (result.isUsed) {
            PubSub.publish("show_info_item_usage", {
                whereUsed: result.whereUsed,
                title: translate("OTM_CANNOT_DELETE_SECTION"),
                initialLabel: translate("OTM_DELETE_SECTION_CHECK_INITIAL_TEXT")
            });
        }
        else {
            await Forms.deleteSection({
                deploymentId,
                formTemplateId: formTemplate._id,
                sectionId: section._id
            });
            logAmplitudeEvent({
                eventType: 'deletedSection',
                eventProperties: {
                    deploymentId,
                    formTemplateId: formTemplate._id,
                    formTemplateName: formTemplate.name,
                    sectionName: selectedSection
                }
            });
            PubSub.publish('refresh-form-template');
        }
        setShowDeleteSection(false);
        setIsLoading(false);
    }, [deploymentId, formTemplate, selectedSection, logAmplitudeEvent, isSectionUsedInFormConditions, translate]);

    const handleOpenLivePreview = useCallback(() => {
        logAmplitudeEvent({
            eventType: 'clickedTestForm',
            eventProperties: {}
        });

        if (!livePreviewWindow || livePreviewWindow.closed) {
            const openedWindow = window.open(livePreviewUrl, '_blank');
            setLivePreviewWindow(openedWindow);
            // Timeout here before sending update so that the OTM app has time to setup the listeners.
            setTimeout(() => sendLivePreviewUpdate(openedWindow), 2000);
        }
        else {
            sendLivePreviewUpdate(livePreviewWindow);
        }
    }, [livePreviewWindow, livePreviewUrl, sendLivePreviewUpdate, logAmplitudeEvent]);

    const renderTemplateNameStatus = useMemo(() => {
        if (!formTemplate) { return null; }

        if (guestMode) {
            return <div className="d-flex align-items-center">
                <label className="h6 ms-2 mb-0">{formTemplate.name}</label>
            </div>;
        }

        const statusInfo = {
            inProgress: { iconName: "Warning", iconClass: "text-warning", labelText: translate("OTM_FORM_TEMPLATE_IN_PROGRESS") },
            published: { iconName: "CheckCircle", iconClass: "text-success", labelText: '' }
        };

        let type = "published";
        if (!formTemplate.isPublished) {
            type = "inProgress";
        }

        return <div className="d-flex align-items-center">
            <Icon icon={statusInfo[type].iconName} className={statusInfo[type].iconClass} />
            <label className="h6 ms-2 mb-0">{formTemplate.name} - {translate("OTM_VERSION")} {formTemplate.version} {statusInfo[type].labelText}</label>
        </div>;
    }, [formTemplate, guestMode, translate]);

    const renderTemplateHeader = useMemo(() => {
        if (formTemplate) {
            return <div className="card border border-primary">
                <div className="card-body d-flex flex-column">
                    <div className="row mt-n2 align-items-center justify-content-end">
                        <div className="col-12 col-lg mt-2">
                            {renderTemplateNameStatus}
                        </div>
                        <div className="col-auto mt-2">
                            <div className="row gx-2 align-items-center justify-content-end">
                                {!guestMode && <div className="col-auto">
                                    <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={() => { setShowTemplateVersionHistory(true) }}
                                    >
                                        {translate("OTM_MANAGE_FORM_TEMPLATE_VERSIONS")}
                                    </Button>
                                </div>}
                                {permissions.update && <div className="col-auto">
                                    <Button
                                        id="btn_test_form"
                                        size="sm"
                                        variant="success"
                                        onClick={handleOpenLivePreview}
                                        disabled={isLoading || !livePreviewUrl || !formTemplate || !formTemplate._id || !allowPublish.allow}
                                    >{translate("OTM_TEST_FORM")}</Button>
                                </div>}
                                {!formTemplate.isPublished && !guestMode && updatePermissions && <div className="col-auto ">
                                    <Button
                                        size="sm"
                                        variant="success"
                                        onClick={() => { setShowPublishTemplate(true); }}
                                        disabled={!allowPublish.allow}
                                    >{translate("OTM_PUBLISH")}</Button>
                                </div>}
                                <div id="btn_edit_template" className="col-auto">
                                    <Button
                                        size="sm"
                                        variant="outline-primary"
                                        className="p-0 border-0"
                                        onClick={() => { setShowEditFormTemplate(true); }}
                                        disabled={isLoading}
                                        title={translate("OTM_EDIT")}
                                    ><Icon icon="Settings" className="d-block" /></Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>;
        }
        else {
            return null;
        }
    }, [guestMode, formTemplate, renderTemplateNameStatus, isLoading, permissions, livePreviewUrl, updatePermissions, handleOpenLivePreview, translate, allowPublish]);

    const forceMaxWidth = useMemo(() => {
        var below3Col = false;
        if (!formTemplate) {
            return false;
        }
        const section = formTemplate.sections.find(({ _id }) => _id === selectedSectionId);
        if (!section || !section.items || section.items.length === 0) {
            return false;
        }
        section.items.forEach(item => {
            if (item.columnSpanLarge < 3) {
                below3Col = true;
            }
        });

        return below3Col;
    }, [formTemplate, selectedSectionId]);

    const renderAlerts = useMemo(() => {
        const alerts = [];

        if (!allowPublish.allow && allowPublish.message) {
            alerts.push(
                <Alert variant="warning" className="mt-3 mb-0" key="alertMessage">
                    <span className="fw-bold">{allowPublish.message}</span>
                </Alert>
            );
        }

        if (forceMaxWidth) {
            alerts.push(
                <Alert variant="warning" className="mt-3 mb-0" key="alertMaxWidth">
                    <span className="fw-bold">{translate("OTM_BELOW_3_DESKTOP_COLUMNS")}</span>
                </Alert>
            );
        }

        return alerts;
    }, [allowPublish, forceMaxWidth, translate]);

    const renderSectionHeader = useMemo(() => {
        if (formTemplate) {
            return <SectionHeader
                section={formTemplate.sections.find((section) => section.name === selectedSection)}
                setSelectedSection={setSelectedSection}
                sections={formTemplate.sections}
                isPublished={formTemplate.isPublished}
                isLoading={isLoading}
                deletePermissions={deletePermissions}
                updatePermissions={updatePermissions}
                setShowAddSection={setShowAddSection}
                setShowEditSection={setShowEditSection}
                setShowDeleteSection={setShowDeleteSection}
                setCopiedSection={setCopiedSection}
            />;
        }
        else {
            return null;
        }
    }, [formTemplate, selectedSection, isLoading, updatePermissions, deletePermissions]);

    const allSectionNames = useMemo(() => {
        if (formTemplate?.sections && selectedSectionId) {
            return formTemplate.sections.map(({ name }) => name)
        }

        return [];
    }, [formTemplate, selectedSectionId]);

    const unselectedSectionNames = useMemo(() => {
        if (formTemplate?.sections && selectedSectionId) {
            return formTemplate.sections.filter(({ _id }) => _id !== selectedSectionId).map(({ name }) => name)
        }

        return [];
    }, [formTemplate, selectedSectionId]);

    return <>
        <DisplayItemUsageInConditions />
        {showTemplateVersionHistory && <TemplateVersionHistory
            show={showTemplateVersionHistory}
            onClose={() => { setShowTemplateVersionHistory(false) }}
        />}
        {showPublishTemplate &&
            <PublishTemplate
                show={showPublishTemplate}
                formTemplate={formTemplate}
                onUpdate={handlePublishTemplate}
                onClose={() => { setShowPublishTemplate(false); }}
            />}
        {showEditFormTemplate &&
            <EditFormTemplate
                formTemplateId={formTemplate._id}
                show={showEditFormTemplate}
                allowUpdate={updatePermissions}
                onUpdate={() => { PubSub.publish('refresh-form-template'); setShowEditFormTemplate(false) }}
                onClose={() => { setShowEditFormTemplate(false) }} />
        }
        {showAddSection &&
            <EditSection
                formTemplateId={formTemplate._id}
                show={showAddSection}
                allowUpdate={updatePermissions}
                isEdit={false}
                defaultShowSave={!!copiedSection.name}
                section={copiedSection}
                sectionNames={allSectionNames}
                onAdd={(section) => {
                    PubSub.publish('refresh-form-template');
                    setSelectedSection(section.name);
                    setShowAddSection(false);
                    setCopiedSection({});
                }}
                onClose={() => {
                    setShowAddSection(false);
                    setCopiedSection({});
                }}
            />
        }
        {selectedSection && showEditSection &&
            <EditSection
                formTemplateId={formTemplate._id}
                isEdit={true}
                section={formTemplate.sections.find((section) => section.name === selectedSection)}
                sectionNames={unselectedSectionNames}
                show={showEditSection}
                allowUpdate={updatePermissions}
                onUpdate={() => { PubSub.publish('refresh-form-template'); setShowEditSection(false) }}
                onClose={() => { setShowEditSection(false) }} />
        }
        {showDeleteSection &&
            <ConfirmationPrompt show={showDeleteSection} message={translate("OTM_CONFIRM_DELETE_SECTION")}
                onConfirm={handleDeleteSection}
                onCancel={() => { setShowDeleteSection(false); }} />
        }
        {renderTemplateHeader}
        {renderAlerts}
        {renderSectionHeader}
        {(formTemplate && formTemplate.sections.find(({ _id }) => _id === selectedSectionId)) ? <SectionItems
            sectionId={selectedSectionId}
            forceMaxWidth={forceMaxWidth}
            isShared={false}
            isReadOnly={formTemplate.isPublished || !updatePermissions}
            refreshTemplate={async () => {
                await getTemplate({ formTemplateName, formTemplateVersion });
            }}
            deletePermissions={deletePermissions}
        /> : null}
    </>
}

const SectionHeader = ({
    section,
    setSelectedSection,
    sections,
    isPublished,
    isLoading,
    deletePermissions,
    updatePermissions,
    setShowAddSection,
    setShowEditSection,
    setShowDeleteSection,
    setCopiedSection
}) => {
    const [translate] = useTranslation();
    const [, drag] = useDrag(() => ({
        type: !section ? FORMS_DND_ITEMS.NULL : FORMS_DND_ITEMS.SECTION,
        item: {
            ...(section || {}),
            _storage: {
                _id: section?._id
            },
            _dragging: {}
        }
    }), [section]);
    const [{ canDrop }, drop] = useDrop({
        accept: updatePermissions ? FORMS_DND_ITEMS.SECTION_ANY : FORMS_DND_ITEMS.NULL,
        collect: (monitor) => ({
            canDrop: !isPublished && monitor.canDrop()
        }),
        drop: async (droppedItem) => {
            const idReplacement = {};
            sections.forEach((section) => {
                idReplacement[section._id] = section._id;

                section.items.forEach((item) => {
                    idReplacement[item._id] = item._id;
                });
            });
            setCopiedSection(copySection(droppedItem, idReplacement));
            setShowAddSection(true);

            return { location: 'section-header' };
        }
    }, [isPublished]);

    if (canDrop) {
        return (
            <div ref={drop} className={`card my-3 btn btn-sm btn-dropzone-forms droppable w-100`}>
                <div className="card-body">
                    <span>{translate('OTM_FORMS_SECTION_DROP')}</span>
                </div>
            </div>
        );
    }

    return (
        <div ref={section && drag} className="card border border-secondary my-3 moveable">
            <div className="card-body d-flex flex-column">
                <div className="row mt-n2 align-items-center justify-content-end">
                    <div className="col-auto mt-2 p-0 order-last order-lg-first">
                        <Icon icon="DragIndicatorOutlined" className="text-tertiary" />
                    </div>
                    <div className="col-12 col-lg mt-2 d-lg-flex align-items-lg-center">
                        <label className="h6 me-lg-2 mb-1 mb-lg-0">{translate("OTM_SECTION")}</label>
                        <div className="flex-grow-1">
                            <GenericDropdown
                                defaultValue={section?.name}
                                options={[...sections.map(({ name }) => ({ value: name }))]}
                                readOnly={isLoading}
                                onSelect={(value) => {
                                    setSelectedSection(value);
                                }}
                                css={{ toggle: "bg-white" }}
                                grow={true}
                            />
                        </div>
                    </div>
                    <div className="col-auto mt-2">
                        <div className="row gx-2 align-items-center justify-content-end">
                            {!isPublished && updatePermissions && (
                                <div className="col-auto">
                                    <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={() => {
                                            setShowAddSection(true);
                                        }}
                                    >
                                        {translate("OTM_ADD_SECTION")}
                                    </Button>
                                </div>
                            )}
                            {!isPublished && updatePermissions && (
                                <div className="col-auto">
                                    <Button
                                        size="sm"
                                        variant="outline-primary"
                                        className="p-0 border-0"
                                        onClick={() => {
                                            const idReplacement = {};
                                            sections.forEach((section) => {
                                                idReplacement[section._id] = section._id;

                                                section.items.forEach((item) => {
                                                    idReplacement[item._id] = item._id;
                                                });
                                            });
                                            setCopiedSection(copySection(section, idReplacement));
                                            setShowAddSection(true);
                                        }}
                                        disabled={isLoading}
                                        title={translate("OTM_FORMS_SECTION_COPY")}
                                    ><Icon icon="FileCopy" className="d-block" /></Button>
                                </div>
                            )}
                            <div id="btn_edit_section" className="col-auto">
                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    className="p-0 border-0"
                                    onClick={() => {
                                        setShowEditSection(true);
                                    }}
                                    disabled={isLoading || !section}
                                    title={translate("OTM_EDIT")}
                                ><Icon icon="Settings" className="d-block" /></Button>
                            </div>
                            {!isPublished && deletePermissions && <div className="col-auto">
                                <Button
                                    size="sm"
                                    variant="outline-danger"
                                    className="p-0 border-0"
                                    onClick={() => {
                                        setShowDeleteSection(true);
                                    }}
                                    disabled={!section}
                                    title={translate("OTM_DELETE")}
                                ><Icon icon="Delete" className="d-block" /></Button>
                            </div>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormTemplateEditor;